/**
 * 이상한 방 - 구획 배정 (누가 어느 구획을 고치는가)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조 - 바꾸기) · 4.5절(점수 - 숨긴 것이 안 들키면 득점)
 *            `이상한방-상태모델-v0.1.md` 5절(변화 규칙) · 9.5절(방을 외우는 것이 스킬) · 11.3절(서버가 쥐는 것)
 *            로드맵 M11(각자 방 - 전원이 자기 구획을 고치고 남의 구획을 찾는다)
 *
 * ## 역할이 사라진 자리에 배정이 온다
 *
 * M10까지는 라운드마다 한 명이 다녀간 사람이고 나머지가 찾았다(`changerId`). 그 구조의 값은 "기다리는 사람"이었다 -
 * 여섯이 앉으면 다섯이 60초 동안 화면을 보고만 있었다. M11은 그 자리를 **구획 배정**으로 바꾼다: 좌석마다 구획 하나,
 * 전원이 동시에 자기 구획을 고치고, 수색에서는 **남의 구획만** 찾는다.
 *
 * 그래서 이 파일의 계약은 하나다 - **좌석 하나에 구획 하나, 남거나 모자라지 않는다.** 평면도가 그것을 보장하고
 * (`core/partyPlan`은 좌석 수만큼 구획을 굽는다) 여기는 짝만 짓는다.
 *
 * ## 결정론 - 방 시드에서 나오고 라운드마다 한 칸 돈다
 *
 * 자리 순서는 `${seed}:zones` 하나에서 나온다(라운드가 들어가지 않는다). 매치 내내 **구획의 순서가 고정**이어야
 * "다음 라운드엔 내가 저 방"이 예측 가능한 약속이 되고, 그것이 방을 외우는 스킬(9.5절)과 같은 종류의 재미다.
 * 라운드는 그 고정된 순서 위에서 **회전량**으로만 쓰인다 - `owner(i) = seats[(i + round) % n]`.
 * 회전이 있어야 한 판 내내 같은 사람이 같은 방을 갖지 않고(그 사람만 그 방을 외운다), 회전이 한 칸씩이어야
 * "내 다음 방"이 옆 사람의 이번 방이라는 것을 화면 없이도 안다.
 *
 * ## 좌석 수와 구획 수가 어긋나면 짝만큼만 짓는다
 *
 * 던지지 않는다. 이 어긋남은 평면도가 보장하므로 **일어나지 않는 일**이지만, 리듀서가 던지면 그 방의 서버 프로세스가
 * 내려간다(M6 기록의 `guard`가 막는 그 사고). 짝만큼만 짓고 남는 쪽은 조용히 빈다 - 그 판은 이상하지만 살아 있다.
 *
 * 이 파일은 `core`·`game` 리프만 본다(`boundary.test.ts`) - 서버가 그대로 import 한다.
 */

import type { CellRect, FloorPlan, Room, RoomId } from '../../core/floorPlan'
import { createRng } from '../../core/rng'
import type { PlayerId } from '../../core/types'

/* --- 타입 ------------------------------------------------------------------------ */

/**
 * 구획 하나와 그 주인. **바꾸기 중에도 전원에게 공개**다(로드맵 M11 규칙 3) - 누가 어디를 손대는지는 알아도
 * 무엇을 했는지는 모른다. 누가 어디인지를 감추면 수색이 "누구 구획인지 모르는 방을 도는 일"이 되어
 * "내 구획은 못 쏜다"가 우연한 벌처럼 보인다.
 *
 * `rect`는 구획의 **내부**(벽 제외)다 - `core/floorPlanModel.Room.rect` 그대로이고, 그 사각형이 곧
 * `core/verbs.VerbContext.allowedRect`가 된다(경계가 두 곳에 적히지 않게 값을 그대로 넘긴다).
 */
export type ZoneAssignment = Readonly<{
  zoneId: RoomId
  ownerId: PlayerId
  rect: CellRect
}>

/* --- 상수 ------------------------------------------------------------------------ */

/**
 * 구획 순서 스트림의 접미사. 방 시드(`seed`)·변화 시드(`${seed}:r${round}`)·봇 스트림(`${seed}:bot:…`)과 갈라
 * 구획 순서가 다른 것들의 소비량에 흔들리지 않게 한다(`changeGen`의 `:changes`와 같은 원리).
 */
const ZONE_STREAM_SUFFIX = ':zones'

/* --- 배정 ------------------------------------------------------------------------ */

/**
 * 좌석 → 구획. 같은 (시드, 라운드, 좌석 순서, 평면도)는 항상 같은 표를 만든다.
 *
 * @param seed     방 시드. 라운드는 섞기에 들어가지 않는다(머리말 - 순서는 매치 내내 고정, 라운드는 회전량).
 * @param round    회전량. 로비(0)에서도 부를 수 있게 음수만 아니면 된다.
 * @param seatIds  좌석 순서(`matchSelectors.seatsInOrder`의 id). 순서가 곧 회전의 방향이다.
 * @param plan     이 판의 평면도. 구획 수는 좌석 수와 같아야 한다(`core/partyPlan`).
 */
export function assignZones(
  seed: string,
  round: number,
  seatIds: readonly PlayerId[],
  plan: FloorPlan,
): readonly ZoneAssignment[] {
  const seats = seatIds.length
  if (seats === 0 || plan.rooms.length === 0) return []

  const order = createRng(`${seed}${ZONE_STREAM_SUFFIX}`).shuffle([...plan.rooms])
  const turn = ((Math.trunc(round) % seats) + seats) % seats

  const assignments: ZoneAssignment[] = []
  // 짝만큼만(머리말). 평면도가 보장하므로 실제로는 항상 `min === seats === rooms`다.
  const pairs = Math.min(seats, order.length)
  for (let index = 0; index < pairs; index += 1) {
    const room = order[index]
    const ownerId = seatIds[(index + turn) % seats]
    if (room === undefined || ownerId === undefined) continue
    assignments.push({ zoneId: room.id, ownerId, rect: room.rect })
  }
  return assignments
}

/* --- 조회 ------------------------------------------------------------------------ */

/** 이 사람의 구획. 없으면 `undefined`(좌석보다 구획이 적은 판 - 머리말). */
export function zoneOf(zones: readonly ZoneAssignment[], id: PlayerId): ZoneAssignment | undefined {
  return zones.find((zone) => zone.ownerId === id)
}

/*
 * "이 칸이 누구의 구획인가"를 여기에 두지 않는다. M11 초안에는 `ownerAtCell`이 있었지만 부르는 자리가 하나도 없었고,
 * 본문은 `core/floorPlanModel.rectContains`를 그대로 다시 적은 것이었다(같은 마일스톤의 `core/verbs`는 그 함수를 부른다).
 * 주인이 필요한 자리 - 리빌의 "누구 구획이었나" - 는 좌표로 되짚지 않고 호스트가 굳힌 `MatchChange.owner`를 읽는다.
 * 좌표에서 주인을 유도해야 할 날이 오면 `rectContains`로 적는다.
 */

/**
 * 그 구획 하나만 담은 평면도.
 *
 * 마감 채움(`matchPhases.fillZones`)이 생성기에게 "이 구획 안에서만 만들라"고 말하는 수단이다. 생성기에 구획 인자를
 * 새로 내지 않은 이유가 규칙이다 - 생성기는 이미 `plan.rooms`를 배분 축(`zoneBag`·`zonePools`·`fitsZone`)으로 쓰므로,
 * 구획 하나짜리 평면도를 주면 **기존 규칙 그대로** 그 안에서만 만든다. 새 인자를 내면 배분 코드가 두 갈래가 된다.
 *
 * `bounds`·`doorways`·`windows`는 그대로 둔다 - 경계는 집 전체의 것이고(물건이 벽을 뚫는 판정은 변하지 않는다),
 * 구획 밖으로 나가는 것은 `allowedRect`가 아니라 `fitsZone`이 이미 막는다.
 */
export function zonePlanOf(plan: FloorPlan, zoneId: RoomId): FloorPlan | null {
  const room: Room | undefined = plan.rooms.find((candidate) => candidate.id === zoneId)
  if (room === undefined) return null
  return { ...plan, rooms: [room] }
}
